import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import orderBy from "lodash/orderBy";
import uniq from "lodash/uniq";
import { useParams, useNavigate } from "react-router";

import useMutation from "../../../hooks/useMutation";
import { useShopping } from "../../../contexts/shopping";
import useNotification from "../../../hooks/notification";
import { usePermissions } from "../../../contexts/permissions";
import {
  MAKE_SHOPPING_INVENTORY_REQUEST_MUTATION,
  MAKE_SHOPPING_INVENTORY_REQUEST_ASYNC_MUTATION,
} from "../../../constants/graphql/mutations";
import { ADD_INVENTORY_REQUEST_ATTACHMENT_MUTATION } from "../../../constants/graphql/mutations";
import { isStartDateGreaterEndDate } from "./WizardCheckout/Step2";
import { useCustomFields } from "../../../contexts/customFields";
import { validateMultipleEmails } from "./CartForm";

const CART_FORM_STORAGE_KEY_PREFIX = "shopping_cart_form_data:v1";

const buildCartFormStorageKey = (user, inventoryRequestId) => {
  const companyId = user?.company?.id || "unknown_company";
  const userScope = user?.accessLink?.id || user?.id || "unknown_user";
  const cartScope = inventoryRequestId || "default_cart";
  return `${CART_FORM_STORAGE_KEY_PREFIX}:${companyId}:${userScope}:${cartScope}`;
};

const loadCartFormCache = storageKey => {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const saveCartFormCache = (storageKey, values) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey, JSON.stringify(values));
  } catch (_error) {
    // noop – caching should never break checkout
  }
};

const clearCartFormCache = storageKey => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(storageKey);
  } catch (_error) {
    // noop
  }
};

// Only persist non-identifying draft fields (avoid files / non-serializable
// values, and avoid caching contact PII in clear text in localStorage).
const pickCartFormValuesForCache = values => ({
  comment: values?.comment,
  deliveryDate: values?.deliveryDate,
  startDate: values?.startDate,
  startTime: values?.startTime,
  endDate: values?.endDate,
  endTime: values?.endTime,
  designer: values?.designer,
  configurableField1: values?.configurableField1,
  configurableField2: values?.configurableField2,
  configurableField3: values?.configurableField3,
  pickingNotes: values?.pickingNotes,
  location: values?.location,
});

function CartFormLocalCache({ storageKey, enabled = true }) {
  const { values, isSubmitting, status } = useFormikContext();
  const latestValuesRef = useRef(values);
  const timeoutRef = useRef(null);
  const orderPlaced = status?.success === true;

  useEffect(() => {
    latestValuesRef.current = values;
  }, [values]);

  // Debounced saves while typing.
  useEffect(() => {
    if (!enabled || isSubmitting || orderPlaced) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      saveCartFormCache(storageKey, pickCartFormValuesForCache(latestValuesRef.current));
    }, 200);

    return () => clearTimeout(timeoutRef.current);
  }, [enabled, isSubmitting, orderPlaced, storageKey, values]);

  // Flush immediately on refresh / tab close so we don't race the debounce.
  useEffect(() => {
    if (!enabled || orderPlaced) return;
    const handleBeforeUnload = () => {
      saveCartFormCache(storageKey, pickCartFormValuesForCache(latestValuesRef.current));
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [enabled, orderPlaced, storageKey]);

  return null;
}

const vaStatus = {
  canceled: false,
  completed: false,
  pending_approval: false,
  picked: false,
  scheduled: false,
  submitted: false,
  change_order: false,
  reserved: true,
};

export const getLocationNotes = (locationNotes, locationOrder) =>
  orderBy(
    Object.entries(locationNotes).map(([location, note], index) => ({
      location,
      note,
      position: locationOrder[location],
    })),
    "position"
  );

const validate = ({ config, wizardMode, isGuest, display, isContractWarehouse }) => values => {
  const errors = {};
  if (config.includes("requestor_details")) {
    if (!values.designer) {
      errors.designer = "Required";
    }
    if (display.ir_configurable_field_3 && !values.configurableField3) {
      errors.configurableField3 = "Required";
    }
  }
  if (config.includes("delivery_details")) {
    if (!values.contactName && display.ir_contact_name) {
      errors.contactName = "Required";
    }
    if (!values.contactPhoneNumber && display.ir_contact_phone_number) {
      errors.contactPhoneNumber = "Required";
    }
      if (display.ir_cc_emails && !values.ccEmails) {
        errors.ccEmails = "Required";
      } else if (display.ir_cc_emails && values.ccEmails && !validateMultipleEmails(values.ccEmails)) {
        errors.ccEmails = "Invalid email format. Use comma or semicolon to separate multiple emails.";
      }

    if (wizardMode) {
      if (!values.startDate && !values.startTime) {
        errors.startDate = "Required";
      }
    } else {
      if (!values.comment) {
        errors.comment = "Required";
      }
      if (display.ir_configurable_field_1 && !isContractWarehouse && !values.configurableField1) {
        errors.configurableField1 = "Required";
      }
      if (display.ir_configurable_field_2 && !isContractWarehouse && !values.configurableField2) {
        errors.configurableField2 = "Required";
      }
      if (display.ir_configurable_field_3 && !values.configurableField3) {
        errors.configurableField3 = "Required";
      }
      if (display.ir_start_date && !values.startDate && !values.startTime) {
        errors.startDate = "Required";
      }
      if (display.ir_delivery_date && !values.deliveryDate && !isGuest) {
        errors.deliveryDate = "Required";
      }
    }

    if (isStartDateGreaterEndDate(values)) {
      errors.startDate = "Must be less than end date";
    }
  }
  if (config.includes("resources")) {
    if (values.resources.some(v => !v.quantity)) {
      errors.resources = [];
      values.resources.forEach((v, i) => {
        if (!v.quantity) {
          errors.resources[i] = { quantity: "Required" };
        }
      });
    }
  }

  return errors;
};

const EnableValidationContext = React.createContext(null);

export default function CartFormWizard({
  setErrors,
  initialValues,
  children,
  setSubmitting,
  wizardConfig,
}) {
  const [enableValidation, setEnableValidation] = useState(false);
  const [mutationInProgress, setMutationInProgress] = useState(false);
  const { projectId } = useParams();
  const { notifySuccess } = useNotification();
  const [makeShoppingInventoryRequest] = useMutation(MAKE_SHOPPING_INVENTORY_REQUEST_MUTATION);
  const [makeShoppingInventoryRequestAsync] = useMutation(
    MAKE_SHOPPING_INVENTORY_REQUEST_ASYNC_MUTATION
  );
  const { inventoryItems, inventoryRequest } = useShopping();
  const cartProjectIds = useMemo(
    () => uniq(Object.values(inventoryItems).map(item => item.project.id)),
    [inventoryItems]
  );
  const cartProjectId = useMemo(
    () => (cartProjectIds.length === 1 ? cartProjectIds[0] : undefined),
    [cartProjectIds]
  );
  const [display] = useCustomFields({ projectId: cartProjectId });
  const [inventoryRequestAddAttachment] = useMutation(ADD_INVENTORY_REQUEST_ATTACHMENT_MUTATION);
  const { user } = usePermissions();
  const wizardMode = user.company.settings.wizard_mode;
  const isContractWarehouse = user.company.settings.simple_checkout_for_contract_warehouse;
  const isGuest = user.role === "guest";
  const navigate = useNavigate();
  const validation = enableValidation
    ? validate({ config: wizardConfig, wizardMode, isGuest, display, isContractWarehouse })
    : undefined;

  const storageKey = useMemo(
    () => buildCartFormStorageKey(user, inventoryRequest?.id),
    [inventoryRequest?.id, user?.company?.id, user?.accessLink?.id, user?.id]
  );
  const [cachedValues, setCachedValues] = useState(() => loadCartFormCache(storageKey));

  useEffect(() => {
    setCachedValues(loadCartFormCache(storageKey));
  }, [storageKey]);
  const mergedInitialValues = useMemo(
    () => ({
      ...initialValues,
      ...cachedValues,
    }),
    [initialValues, cachedValues]
  );

  const onSubmit = (values, { setErrors: setFormErrors, setStatus }) => {
    // Prevent duplicate mutations (but allow re-validation)
    if (mutationInProgress) {
      return;
    }

    const { files, locationNotes, locationOrder = {}, ...input } = values;
    input.hasCheckoutAttachments = Array.isArray(files) && files.length > 0;
    const urlParams = new URLSearchParams(window.location.search);
    const reserve = urlParams.get("reserve") === "true";

    if (reserve) {
      input.vaStatus = vaStatus;
    } else {
      input.vaStatus = {
        canceled: false,
        completed: false,
        pending_approval: false,
        picked: false,
        scheduled: false,
        submitted: true,
        reserved: false,
      };
    }

    if (locationNotes) {
      input.locationNotes = getLocationNotes(locationNotes, locationOrder);
    }

    setSubmitting(true);
    setMutationInProgress(true);

    const itemsCount = Object.keys(inventoryItems || {}).length;
    const companyThresholdRaw = user?.company?.settings?.async_order_processing_items_threshold;
    const companyThreshold = Number.parseInt(companyThresholdRaw, 10);
    const threshold = Number.isNaN(companyThreshold) ? 50 : companyThreshold;
    const useAsync = itemsCount > threshold;

    const mutation = useAsync ? makeShoppingInventoryRequestAsync : makeShoppingInventoryRequest;

    return mutation({
      variables: { input, projectId, inventoryRequestId: inventoryRequest?.id },
      onSuccess: ({ inventoryRequest } = {}) => {
        setSubmitting(false);
        setMutationInProgress(false);
        setStatus({ success: true });

        // Clear the form cache after successful order placement
        clearCartFormCache(storageKey);
        setCachedValues({});

        if (useAsync) {
          notifySuccess("Order has been submitted!");
          navigate("/shopping/cart/submitted");
          return;
        }

        notifySuccess("Order has been successfully created!");

        const moveTo = () => {
          if (reserve) {
            return;
          }

          const workOrderNumber = encodeURIComponent(inventoryRequest.workOrderNumber);
          const confirmationUrl = `/shopping/cart/confirmation?id=${inventoryRequest.id}&workOrderNumber=${workOrderNumber}`;
          navigate(confirmationUrl);
        };

        if (files) {
          return Promise.all(
            files.map(file =>
              inventoryRequestAddAttachment({
                variables: { inventoryRequestId: inventoryRequest.id, file },
              })
            )
          ).then(() => {
            moveTo();
          });
        } else {
          moveTo();
        }
      },
      onFailure: errors => {
        setStatus({ success: false });
        setFormErrors(errors);
        if (setErrors) setErrors(errors);
        setSubmitting(false);
        setMutationInProgress(false);
      },
    });
  };

  return (
    <EnableValidationContext.Provider value={setEnableValidation}>
      <Formik
        enableReinitialize={true}
        initialValues={mergedInitialValues}
        validate={validation}
        onSubmit={onSubmit}
      >
        <Form>
          <CartFormLocalCache storageKey={storageKey} />
          {children}
        </Form>
      </Formik>
    </EnableValidationContext.Provider>
  );
}

export const useEnableValidation = () => React.useContext(EnableValidationContext);
